import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import {
  UploadCloud, Loader2, X, FileText, Sparkles, AlertTriangle, Check, ChevronRight,
  ShieldAlert, Coins, Compass, Users, Truck, ClipboardCheck,
} from "lucide-react";

const SECTIONS = [
  { key: "risques", label: "Risques", icon: ShieldAlert, color: "#E24B4A",
    fields: [["title","Titre"],["categorie","Catégorie"],["niveau","Niveau"]] },
  { key: "financement_actifs", label: "Actifs (Financement)", icon: Coins, color: "#1D9E75",
    fields: [["nom","Nom"],["valeur","Valeur"],["devise","Devise"],["ltv","LTV"],["classe","Classe"]] },
  { key: "origination_opportunites", label: "Opportunités (Origination)", icon: Compass, color: "#7F77DD",
    fields: [["titre","Titre"],["secteur","Secteur"],["ticket_mEUR","Ticket M€"],["etape","Étape"]] },
  { key: "rh_postes", label: "Postes RH", icon: Users, color: "#378ADD",
    fields: [["poste","Poste"],["departement","Département"],["criticite","Criticité"]] },
  { key: "fournisseurs", label: "Fournisseurs", icon: Truck, color: "#EF9F27",
    fields: [["nom","Nom"],["categorie","Catégorie"],["criticite","Criticité"]] },
  { key: "audits", label: "Audits", icon: ClipboardCheck, color: "#A1A1AA",
    fields: [["titre","Titre"],["referentiel","Référentiel"],["date_audit","Date"]] },
];

function fmtErr(d) {
  if (!d) return "Erreur.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function ImportIntelligentModal({ onClose, onApplied }) {
  const [step, setStep] = useState("upload"); // upload | analyzing | preview | applying | done
  const [file, setFile] = useState(null);
  const [hint, setHint] = useState("");
  const [error, setError] = useState("");
  const [detection, setDetection] = useState(null);
  const [filename, setFilename] = useState("");
  const [selected, setSelected] = useState({}); // { sectionKey: Set(_tid) }
  const [report, setReport] = useState(null);
  const inputRef = useRef(null);

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    setError("");
  };

  const analyze = async (e) => {
    e?.preventDefault();
    if (!file) { setError("Veuillez sélectionner un document."); return; }
    setStep("analyzing"); setError("");
    try {
      // P0 — async polling pour bypasser le timeout K8s 60s sur les gros fichiers.
      // 1) Start : POST multipart → renvoie job_id immédiatement
      // 2) Poll : GET status/{job_id} toutes les 2s jusqu'à done/error
      const fd = new FormData();
      fd.append("file", file);
      if (hint) fd.append("hint", hint);
      const startRes = await api.post("/workspace/import/analyze/start", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Job non créé.");

      let payload = null;
      const maxAttempts = 90; // ~3 min de plafond
      for (let i = 0; i < maxAttempts; i += 1) {
        await new Promise((res) => setTimeout(res, 2000));
        const st = await api.get(`/workspace/import/analyze/status/${jobId}`);
        if (st.data?.status === "done") { payload = st.data; break; }
        if (st.data?.status === "error") {
          throw new Error(st.data?.error || "Erreur d'analyse.");
        }
      }
      if (!payload) throw new Error("Analyse trop longue — réessayez avec un document plus court.");

      setDetection({ ...payload.detection, _is_image: !!payload.is_image });
      setFilename(payload.filename);
      // Pre-select everything by default
      const sel = {};
      for (const s of SECTIONS) {
        sel[s.key] = new Set((payload.detection[s.key] || []).map((it) => it._tid));
      }
      setSelected(sel);
      setStep("preview");
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail || err.message));
      setStep("upload");
    }
  };

  const toggle = (sectionKey, tid) => {
    setSelected((s) => {
      const next = { ...s };
      const set = new Set(next[sectionKey] || []);
      if (set.has(tid)) set.delete(tid); else set.add(tid);
      next[sectionKey] = set;
      return next;
    });
  };
  const toggleAll = (sectionKey) => {
    setSelected((s) => {
      const items = (detection[sectionKey] || []).map((it) => it._tid);
      const set = new Set(s[sectionKey] || []);
      const allOn = items.every((id) => set.has(id));
      return { ...s, [sectionKey]: new Set(allOn ? [] : items) };
    });
  };

  const apply = async () => {
    setStep("applying"); setError("");
    try {
      const payload = {
        source_doc_name: filename,
        document_type: detection?.document_type || null,
        summary: detection?.summary || null,
        confidence: detection?.confidence ?? null,
        is_image: !!detection?._is_image,
        save_as_project: true,
      };
      for (const s of SECTIONS) {
        payload[s.key] = (detection[s.key] || []).filter((it) => selected[s.key]?.has(it._tid));
      }
      const r = await api.post("/workspace/import/apply", payload);
      setReport(r.data);
      setStep("done");
      onApplied?.(r.data);
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail || err.message));
      setStep("preview");
    }
  };

  const totalSelected = SECTIONS.reduce((acc, s) => acc + (selected[s.key]?.size || 0), 0);

  return (
    <Modal onClose={onClose} maxWidth="max-w-4xl" testid="import-intel-modal">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="noam-overline flex items-center gap-1.5 text-[#7F77DD]"><Sparkles className="w-3 h-3" /> Import intelligent</div>
          <div className="noam-display text-lg font-semibold mt-1">Analyser un document avec NOAM</div>
          <p className="text-[11px] text-zinc-500 mt-1.5">PDF, Word, Excel, CSV, PowerPoint ou image (PNG/JPG/WEBP). L'IA détecte risques, actifs, opportunités, RH, fournisseurs et audits, et sauvegarde l'analyse comme projet.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
        </div>
      )}

      {step === "upload" && (
        <form onSubmit={analyze} className="space-y-4">
          <div onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-white/10 rounded-lg p-10 text-center cursor-pointer hover:border-[#7F77DD]/60 hover:bg-[#7F77DD]/5 transition-colors"
            data-testid="import-dropzone">
            <UploadCloud className="w-8 h-8 mx-auto text-zinc-400" />
            <div className="text-sm text-white mt-3">{file ? file.name : "Cliquer pour sélectionner un document"}</div>
            <div className="text-[11px] text-zinc-500 mt-1">PDF, DOCX, XLSX, CSV, PPTX, PNG, JPG, JPEG, WEBP — max 12 Mo</div>
          </div>
          <input ref={inputRef} type="file" accept=".pdf,.docx,.xlsx,.csv,.pptx,.png,.jpg,.jpeg,.webp" hidden
            onChange={(e) => onFile(e.target.files?.[0])} data-testid="import-file-input" />

          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Indication (facultatif)</label>
            <input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Ex : mémo promoteur immobilier, deal flow Q1, registre risques cyber…"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#7F77DD] focus:outline-none" data-testid="import-hint" />
          </div>

          <button type="submit" disabled={!file} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="import-analyze-btn">
            <Sparkles className="w-3.5 h-3.5" /> Analyser avec NOAM
          </button>
        </form>
      )}

      {step === "analyzing" && (
        <div className="py-12 text-center" data-testid="import-analyzing">
          <Loader2 className="w-8 h-8 text-[#7F77DD] animate-spin mx-auto" />
          <div className="text-white mt-4 text-sm">NOAM analyse votre document…</div>
          <div className="text-[11px] text-zinc-500 mt-1">Extraction de texte → détection structurée multi-modules</div>
        </div>
      )}

      {step === "preview" && detection && (
        <div className="space-y-4" data-testid="import-preview">
          <div className="rounded-md border border-[#7F77DD]/40 bg-[#7F77DD]/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-[#7F77DD]" />
              <span className="text-white text-sm font-medium">{detection.document_type}</span>
              <span className="ml-auto text-[10px] text-[#7F77DD] uppercase tracking-wider">Confiance {Math.round((detection.confidence || 0) * 100)}%</span>
            </div>
            <p className="text-zinc-300 text-xs">{detection.summary || "Document analysé."}</p>
          </div>

          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
            {SECTIONS.map((s) => {
              const items = detection[s.key] || [];
              if (items.length === 0) return null;
              const Icon = s.icon;
              const allOn = items.every((it) => selected[s.key]?.has(it._tid));
              return (
                <div key={s.key} className="border border-white/5 rounded-md overflow-hidden" data-testid={`import-section-${s.key}`}>
                  <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.02]" style={{ borderLeft: `3px solid ${s.color}` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                    <span className="text-white text-xs font-medium">{s.label}</span>
                    <span className="text-[10px] text-zinc-500">({selected[s.key]?.size || 0}/{items.length})</span>
                    <button onClick={() => toggleAll(s.key)} className="ml-auto text-[10px] text-zinc-400 hover:text-white"
                      data-testid={`import-section-toggle-${s.key}`}>
                      {allOn ? "Désélectionner tout" : "Tout sélectionner"}
                    </button>
                  </div>
                  <div className="divide-y divide-white/5">
                    {items.map((it) => {
                      const checked = selected[s.key]?.has(it._tid);
                      return (
                        <label key={it._tid} className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.02]"
                          data-testid={`import-item-${it._tid}`}>
                          <input type="checkbox" checked={!!checked} onChange={() => toggle(s.key, it._tid)} className="mt-0.5 accent-[#7F77DD]" />
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-xs font-medium truncate">
                              {it.title || it.nom || it.titre || it.poste || "Item"}
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                              {s.fields.map(([k, lbl]) => it[k] != null && it[k] !== "" && (
                                <span key={k}><span className="text-zinc-600">{lbl}:</span> <span className="text-zinc-300">{String(it[k])}</span></span>
                              ))}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {SECTIONS.every((s) => (detection[s.key] || []).length === 0) && (
              <div className="text-center text-zinc-500 text-xs py-8 italic">Aucun élément structuré détecté dans ce document.</div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5">Annuler</button>
            <button onClick={apply} disabled={totalSelected === 0} className="btn-primary text-xs disabled:opacity-50" data-testid="import-apply-btn">
              <Check className="w-3.5 h-3.5" /> Appliquer ({totalSelected})
            </button>
          </div>
        </div>
      )}

      {step === "applying" && (
        <div className="py-12 text-center"><Loader2 className="w-8 h-8 text-[#7F77DD] animate-spin mx-auto" /><div className="text-white mt-3 text-sm">Création des entrées dans votre workspace…</div></div>
      )}

      {step === "done" && report && (
        <div className="space-y-4 text-center py-6" data-testid="import-done">
          <div className="w-12 h-12 rounded-full bg-[#1D9E75]/20 border-2 border-[#1D9E75] flex items-center justify-center mx-auto">
            <Check className="w-6 h-6 text-[#1D9E75]" />
          </div>
          <div className="noam-display text-lg font-semibold">Import terminé</div>
          <div className="space-y-1 text-xs text-zinc-300 max-w-xs mx-auto">
            {Object.entries(report.inserted || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/5 py-1.5">
                <span className="text-zinc-400">{k}</span>
                <span className="text-[#1D9E75] font-semibold">+{v}</span>
              </div>
            ))}
            {Object.keys(report.inserted || {}).length === 0 && <div className="italic text-zinc-500">Aucune insertion.</div>}
          </div>
          {(report.rejected || []).length > 0 && (
            <div className="max-w-sm mx-auto text-left rounded border border-amber-500/30 bg-amber-500/5 p-3 space-y-1" data-testid="import-rejected-rows">
              <div className="text-[11px] font-semibold text-amber-400">
                {report.rejected.length} ligne(s) rejetée(s) — rien n'a été perdu silencieusement :
              </div>
              {report.rejected.slice(0, 10).map((r, i) => (
                <div key={i} className="text-[10px] text-zinc-400">
                  <span className="text-zinc-300">{r.section}</span> · ligne {r.row} — {r.reason}
                </div>
              ))}
              {report.rejected.length > 10 && (
                <div className="text-[10px] text-zinc-500 italic">…et {report.rejected.length - 10} autre(s).</div>
              )}
            </div>
          )}
          <button onClick={onClose} className="btn-primary mt-4" data-testid="import-done-close">Fermer</button>
        </div>
      )}
    </Modal>
  );
}

export function ImportButton({ canImport, allowedModules, fullWidth = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!canImport) return null;
  if (allowedModules && !allowedModules.includes("import")) return null;
  return (
    <>
      <button onClick={() => setOpen(true)}
        className={
          fullWidth
            ? "w-full inline-flex items-center gap-2 text-xs text-zinc-200 hover:text-white transition-colors text-left"
            : "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#7F77DD]/40 text-[#B5D4F4] hover:bg-[#7F77DD]/10 transition-colors"
        }
        data-testid="topbar-import-btn">
        <UploadCloud className="w-3.5 h-3.5 text-[#7F77DD]" /> {t("saas.import.button_short", { defaultValue: "Importer" })}
      </button>
      {open && <ImportIntelligentModal onClose={() => setOpen(false)} onApplied={() => setTimeout(() => window.location.reload(), 600)} />}
    </>
  );
}
